import "./custom-editor.scss";
import { ChangeEvent, useEffect, useState } from "react";
import { FormControlChildProps } from "../form-control";
import { Editor } from "react-draft-wysiwyg";
import {
  ContentState,
  EditorState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import StandardizeContentService from "@app/services/http/standardize-content.service";

interface CustomEditorProps extends FormControlChildProps {
  placeholder?: string;

  className?: string;

  value?: string;

  defaultValue?: string;

  defaultStatus?: undefined | "valid" | "inValid" | "warn";

  errorClassName?: string;

  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;

  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;

  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;

  isStandardizeContent?: boolean;
}

const prepareDraft = (value: string) => {
  const draft = convertFromHTML(value);
  const editorState = EditorState.createWithContent(
    ContentState.createFromBlockArray(draft.contentBlocks)
  );

  return editorState;
};

function CustomEditor({
  formik,
  placeholder,
  value = "",
  defaultValue = "",
  name,
  status,
  defaultStatus = undefined,
  errorClassName = "",
  className = "",
  isStandardizeContent = false,
}: CustomEditorProps) {
  const [editorState, setEditorState] = useState<EditorState>(
    value ? prepareDraft(value) : EditorState.createEmpty()
  );
  const [_status, changeStatus] = useState(status || defaultStatus);
  const [isSetted, setIsSetted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (value && !isSetted) {
      setEditorState(prepareDraft(value));
      setIsSetted(true);
    }
  }, [value]);

  const onEditorStateChange = (editorState: EditorState) => {
    const value = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    if (formik) {
      formik.setFieldValue(name!, value);
    }

    setEditorState(editorState);

    setTimeout(() => {
      if (formik) {
        formik.setFieldTouched(name!, true);
      }
    });
  };

  useEffect(() => {
    if (defaultValue) {
      const html = convertFromHTML(defaultValue);
      const editorState = EditorState.createWithContent(
        ContentState.createFromBlockArray(html.contentBlocks, html.entityMap)
      );

      setEditorState(editorState);
    }
  }, [defaultValue]);

  useEffect(() => {
    changeStatus(status || defaultStatus);
  }, [defaultStatus, status]);

  const isError = _status === "inValid" || _status === "warn";

  const handleStandardize = async () => {
    setLoading(true);
    try {
      const html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      const res = await StandardizeContentService.getStandardizeContent(html);

      if (res?.data?.content) {
        const newEditorState = prepareDraft(res.data.content);

        setEditorState(newEditorState);

        if (formik) {
          formik.setFieldValue(name!, res.data.content);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className={`w-full ${className}`}>
        <Editor
          placeholder={placeholder}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          stripPastedStyles={true}
        />
      </div>
      {isStandardizeContent && (
        <div className="flex">
          <button
            type="button"
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-[13px] ml-auto"
            onClick={handleStandardize}
            disabled={loading}
          >
            {loading
              ? "Đang kiểm tra..."
              : "Kiểm tra và sửa lỗi chính tả tự động"}
          </button>
        </div>
      )}
      {isError && (
        <div className={`flex error-color text-xs ${errorClassName}`}>
          {formik &&
            formik.getFieldMeta(name!).error &&
            formik.getFieldMeta(name!).error}
        </div>
      )}
    </div>
  );
}

export default CustomEditor;
